import { useEffect } from 'react'
import { NavLink, Outlet } from 'react-router'
import { useBahasa } from '../../app/i18n'
import { useSesi } from '../../app/auth'
import { useOrangSaya, usePengetahuanSaya, useSambunganSaya } from '../../domains/tenancy/api'
import PemilihBahasa from '../../app/PemilihBahasa'
import Merek from '../../shared/ui/Merek'

const cx = (...a) => a.filter(Boolean).join(' ')

/**
 * Shell Smova Tenant Admin — aksen teal.
 *
 * DIBANGUN SEBAGIAN, dan sengaja. Rancangan 7 September menetapkan delapan menu
 * (Beranda · Chatbot · Meeting · Konten · Pengetahuan · Sambungan · Tim · Pemakaian).
 * Yang ada di sini dua, karena operator memilih membangun bagian TINGKAT 2 lebih dulu
 * supaya langkah berikutnya tidak menunggu seluruh antarmuka selesai.
 *
 * Enam sisanya TIDAK ditampilkan mati di sini — beda dengan konsol operator, di mana
 * kelima menunya sudah punya bentuk yang disepakati dan hanya menunggu backend. Di
 * sini yang belum jelas bukan backendnya melainkan isinya: menu "Chatbot" nanti berisi
 * setelan chatbot, dan sebagian di antaranya justru yang sedang dipindahkan dari
 * `system_settings`. Menampilkan rangka yang isinya masih berpindah-pindah hanya
 * mengajari orang letak yang akan berubah.
 */
// ADMIN TENANT TETAP BISA MEMBUKA LAYAR STAFNYA — rutenya dipertahankan di `App.jsx`,
// cuma tidak lagi jadi menu di sini. Mencabut aksesnya akan jadi penurunan kemampuan yang
// tidak diminta siapa pun; menaruhnya di menu utama membuat panel ini terlihat seperti
// dashboard staf dengan dua setelan menempel, bukan panel admin.
//
// DELAPAN menu dalam TIGA kelompok, mengikuti `DESAIN_BACKOFFICE.md`.
//
// Versi sebelumnya memakai ulang lima layar staf apa adanya lalu menempelkan empat menu
// datar (Fitur, Model, Atur Konten, Field Profil). Itu ditulis sebagai sementara, dan
// memang lebih jujur daripada mencabutnya saat itu — tapi ia menghasilkan panel yang
// bentuknya tidak pernah dirancang siapa pun: admin tenant mendarat di layar STAF, dan
// hal yang di rancangan jadi TAB di dalam Chatbot/Konten berdiri sendiri sebagai menu.
//
// Kelompoknya berarti, bukan hiasan: "Kerja" dibuka tiap kali, "Pengaturan bot" sesekali,
// "Perusahaan" hampir tidak pernah. Menu yang urutannya tidak mencerminkan itu memaksa
// orang memindai seluruh daftar tiap kali.
// `hitung` menyebut ANGKA APA yang ditempel di menu itu — bukan angkanya sendiri.
// Nilainya dihitung di komponen dari kueri yang memang sudah dimuat layar lain, jadi
// menyalakannya tidak menambah satu pun permintaan baru ke backend.
const MENU = [
  { kelompok: 'ta.grupKerja' },
  { ke: '/beranda', kunci: 'ta.navBeranda' },

  { kelompok: 'ta.grupBot' },
  { ke: '/chatbot', kunci: 'ta.navChatbot' },
  { ke: '/atur-meeting', kunci: 'ta.navMeeting' },
  { ke: '/atur-konten', kunci: 'ta.navKonten' },
  { ke: '/pengetahuan', kunci: 'ta.navPengetahuan' , hitung: 'dokumen' },

  { kelompok: 'ta.grupPerusahaan' },
  { ke: '/sambungan', kunci: 'ta.navSambungan' , hitung: 'akun' },
  { ke: '/tim', kunci: 'ta.navTim' , hitung: 'orang' },
  { ke: '/pemakaian-saya', kunci: 'ta.navPemakaian' },
]

export default function ShellTenantAdmin() {
  const { t } = useBahasa()
  const { sesi, keluar } = useSesi()

  // HITUNGAN DI MENU — dan sengaja dari kueri yang SUDAH dipakai layar lain, jadi ia
  // berbagi cache TanStack alih-alih menambah permintaan. Yang gagal atau belum termuat
  // menghasilkan `undefined`, dan lencananya tidak dirender sama sekali: angka yang
  // muncul lalu berubah lebih buruk daripada angka yang belum muncul.
  const dok = usePengetahuanSaya()
  const sambungan = useSambunganSaya()
  const orang = useOrangSaya()

  const hitungan = {
    dokumen: dok.data?.filter((d) => d.is_active).length,
    akun: sambungan.data?.akun?.length,
    // Yang BELUM tertaut, bukan jumlah orang. Menu memberi tahu apa yang butuh perhatian;
    // "4 orang" tidak butuh perhatian, "4 belum tertaut" butuh.
    orang: orang.data?.filter((u) => !u.telegram_user_id).length || undefined,
  }

  useEffect(() => {
    document.documentElement.dataset.shell = 'tenant-admin'
    return () => { delete document.documentElement.dataset.shell }
  }, [])

  return (
    <div className="grid min-h-full md:grid-cols-[214px_minmax(0,1fr)]">
      <nav className="bg-surface border-b md:border-b-0 md:border-r border-line flex flex-col gap-5 py-4 md:sticky md:top-0 md:h-screen">
        <Merek antarmuka={t('app.tenantAdmin')} />

        <div className="flex flex-col gap-px">
          {MENU.map((m) => (
            m.kelompok ? (
              <span
                key={m.kelompok}
                className="px-[18px] pt-3.5 pb-1 text-[10.5px] font-semibold uppercase
                           tracking-[.08em] text-ink3"
              >
                {t(m.kelompok)}
              </span>
            ) : (
            <NavLink
              key={m.ke}
              to={m.ke}
              className={({ isActive }) =>
                cx(
                  'flex items-center justify-between gap-2 px-[18px] py-[7px] text-[13px] border-l-2',
                  isActive
                    ? 'text-ink bg-surface2 border-accent font-semibold'
                    : 'text-ink2 border-transparent font-medium hover:bg-surface2 hover:text-ink',
                )
              }
            >
              <span>{t(m.kunci)}</span>
              {m.hitung && hitungan[m.hitung] !== undefined && (
                <span className="font-mono text-[10.5px] text-ink3 tabular">
                  {hitungan[m.hitung]}
                </span>
              )}
            </NavLink>
          )
          ))}
        </div>

        <div className="mt-auto px-[18px] flex flex-col gap-2.5">
          <PemilihBahasa />
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <b className="block text-[12px] font-semibold truncate">{sesi?.nama}</b>
              <span className="text-[11px] text-ink3">{t(`peran.${sesi?.peran}`)}</span>
            </div>
            <button
              onClick={keluar}
              className="text-[11px] text-ink3 hover:text-ink underline bg-transparent border-0 cursor-pointer p-0"
            >
              {t('umum.keluar')}
            </button>
          </div>
        </div>
      </nav>

      <main className="min-w-0">
        <div className="p-6 max-w-[1240px] w-full flex flex-col gap-5">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
